import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Globe, Facebook, Instagram, Youtube, Save, Camera, Link as LinkIcon, Building, Tv, FileText, CheckCircle2, ShieldAlert, Key, LifeBuoy, MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { XIcon } from "@/components/icons/XIcon";
import { uploadFile } from "@/utils/uploadHelpers";
import { getApiUrl } from "@/utils/apiConfig";
import { fetchModerationHistory, addModerationEntry, createSupportTicket, fetchSupportTickets, ModerationHistoryEntry, SupportTicket } from "@/data/moderationStore";

export default function PartnerProfilePage() {
  const { user, isParty, isOng, isInstitution, isMedia, updateUser, checkAuth } = useAuth();
  const { toast } = useToast();
  
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [unlockSubject, setUnlockSubject] = useState("");
  const [unlockMessage, setUnlockMessage] = useState("");
  const [isUnlockModalOpen, setIsUnlockModalOpen] = useState(false);

  const [supportSubject, setSupportSubject] = useState("");
  const [supportMessage, setSupportMessage] = useState("");
  const [supportFile, setSupportFile] = useState<string | null>(null);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [isSubmittingSupport, setIsSubmittingSupport] = useState(false);
  const [history, setHistory] = useState<ModerationHistoryEntry[]>([]);
  const [supportTickets, setSupportTickets] = useState<SupportTicket[]>([]);
  const [showArchivedSupport, setShowArchivedSupport] = useState(false);
  const API_BASE_URL = getApiUrl();
  const isLocked = user ? (user.modificationsCount || 0) >= 1 && !user.adminAuthorization && user.metadataStatus !== 'rejete' : false;

  const [formData, setFormData] = useState({
    description: "",
    statut: "",
    activites: "",
    contribution: "",
    projets: "",
    website: "",
    facebook: "",
    twitter: "",
    instagram: "",
    youtube: "",
    logoUrl: "",
    image1Url: "",
    image2Url: "",
    // Spécifique ONG
    ongLevel: "",
    ongSiege: "",
    // Spécifique Média
    mediaTitleFr: "",
    mediaTitleAr: "",
    mediaTitleZgh: ""
  });

  useEffect(() => {
    if (user) {
        fetchModerationHistory(user.id).then(setHistory);
        fetchSupportTickets().then(setSupportTickets);
        // Si le profil est en attente, on charge les modifications en attente pour affichage
        const src = user.metadataStatus === 'en_attente' && user.pendingMetadata ? { ...user, ...user.pendingMetadata } : user;
        setFormData({
          description: src.description || "",
          statut: src.statut || "",
          activites: src.activites || "",
          contribution: src.contribution || "",
          projets: src.projets || "",
          website: src.website || "",
          facebook: src.facebook || "",
          twitter: src.twitter || "",
          instagram: src.instagram || "",
          youtube: src.youtube || "",
          logoUrl: src.logoUrl || "",
          image1Url: src.image1Url || "",
          image2Url: src.image2Url || "",
          ongLevel: src.ongLevel || "",
          ongSiege: src.ongSiege || "",
          mediaTitleFr: src.mediaTitleFr || "",
          mediaTitleAr: src.mediaTitleAr || "",
          mediaTitleZgh: src.mediaTitleZgh || ""
        });
    }
  }, [user]);

  const handleSupportFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast({ title: "Fichier trop volumineux", description: "Le fichier ne doit pas dépasser 5MB.", variant: "destructive" });
        return;
      }
      try {
        setIsUploading(true);
        toast({ title: "Upload en cours", description: "Veuillez patienter..." });
        const url = await uploadFile(file);
        setSupportFile(url);
        toast({ title: "Upload réussi", description: "Le fichier a été chargé." });
      } catch (error) {
        toast({ title: "Erreur", description: "Échec du chargement du fichier.", variant: "destructive" });
      } finally {
        setIsUploading(false);
      }
    }
  };

  const submitSupportTicket = async () => {
    if (isSubmittingSupport) return;
    if (!supportSubject || !supportMessage) {
      toast({ title: "Champs requis", description: "Veuillez remplir le sujet et le message.", variant: "destructive" });
      return;
    }
    setIsSubmittingSupport(true);
    const success = await createSupportTicket(supportSubject, supportMessage, supportFile);
    setIsSubmittingSupport(false);
    if (success) {
      toast({ title: "Demande envoyée", description: "Votre message a été transmis au support Kayn'in." });
      setSupportSubject("");
      setSupportMessage("");
      setSupportFile(null);
      setIsSupportModalOpen(false);
      
      fetchSupportTickets().then(setSupportTickets);
      
      if (user?.accreditationStatus === 'rejected' || user?.accreditationStatus === 'requires_proof') {
          await addModerationEntry(user.id, 'PARTNER', 'PARTNER_REACTION', supportMessage, supportFile);
          fetchModerationHistory(user.id).then(setHistory);
      }
    } else {
      toast({ title: "Erreur", description: "Impossible d'envoyer la demande.", variant: "destructive" });
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, field: string) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast({ title: "Fichier trop volumineux", description: "L'image ne doit pas dépasser 5MB.", variant: "destructive" });
        return;
      }
      try {
        setIsUploading(true);
        toast({ title: "Upload en cours", description: "Veuillez patienter..." });
        const url = await uploadFile(file);
        handleChange(field, url);
        toast({ title: "Upload réussi", description: "L'image a été chargée avec succès." });
      } catch (error) {
        console.error("Erreur d'upload:", error);
        toast({ title: "Erreur d'upload", description: "Une erreur est survenue lors du chargement de l'image.", variant: "destructive" });
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveSuccess(false);
    try {
      // Nous envoyons toutes les données de présentation dans l'objet "metadata"
      // Le backend (api/auth.php) l'enregistrera dans la colonne JSON 'metadata'
      const success = await updateUser({
        metadata: formData
      });
      
      if (success) {
        setSaveSuccess(true);
        toast({ title: "Profil mis à jour", description: "Vos informations ont été enregistrées avec succès et sont désormais publiques." });
        
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Une erreur est survenue lors de l'enregistrement.", variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleUnlockRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!unlockSubject.trim() || !unlockMessage.trim() || !user) return;
    
    setIsSaving(true);
    try {
      const token = localStorage.getItem("kaynin_auth_token");
      const res = await fetch(`${API_BASE_URL}/auth.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ action: "request_unlock", id: user.id, subject: unlockSubject, message: unlockMessage })
      });
      if (res.ok) {
        toast({ title: "Demande envoyée", description: "Votre demande de déverrouillage a été transmise à l'administration." });
        setIsUnlockModalOpen(false);
        setUnlockSubject("");
        setUnlockMessage("");
        // Recharge user via auth context
        if (checkAuth) {
          await checkAuth(); 
        }
      } else {
        const errText = await res.text();
        toast({ title: "Erreur", description: "Impossible d'envoyer la demande : " + errText, variant: "destructive" });
      }
    } catch (e) {
      toast({ title: "Erreur", description: "Impossible d'envoyer la demande.", variant: "destructive" });
    }
    setIsSaving(false);
  };

  const roleLabel = isParty ? "du parti politique" : isOng ? "de l'association" : isMedia ? "du média" : isInstitution ? "de l'institution" : "de l'organisation";

  return (
    <div className="max-w-5xl mx-auto space-y-8 animate-in fade-in duration-500 pb-12">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground flex items-center gap-3">
            <Building className="w-8 h-8 text-primary" />
            Profil Public
          </h1>
          <p className="text-muted-foreground font-body mt-1">
            Gérez les informations publiques {roleLabel}. Ces données seront visibles par tous les citoyens.
          </p>
        </div>
        <div className="flex gap-3">
          <Dialog open={isSupportModalOpen} onOpenChange={setIsSupportModalOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2 shrink-0 h-12 px-6 rounded-xl font-bold shadow-sm bg-blue-600 hover:bg-blue-700 text-white border-none">
                <LifeBuoy className="w-5 h-5" />
                Contact Support Kayn'in
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Contacter le Support Kayn'in</DialogTitle>
                <DialogDescription>
                  Un problème avec votre espace ? Vous devez fournir une preuve suite à un rejet ? Remplissez ce formulaire.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <label className="text-sm font-semibold">Sujet</label>
                  <Input value={supportSubject} onChange={e => setSupportSubject(e.target.value)} placeholder="Ex: Erreur lors de l'enregistrement..." />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold">Message ou Explications</label>
                  <Textarea value={supportMessage} onChange={e => setSupportMessage(e.target.value)} placeholder="Décrivez votre problème en détail..." className="h-32" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold flex items-center justify-between">
                    Joindre un fichier (PDF, IMG, WORD) - Optionnel
                    {supportFile && <span className="text-emerald-600 text-xs">✓ Fichier joint</span>}
                  </label>
                  <Input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" onChange={handleSupportFileUpload} disabled={isUploading} />
                </div>
                <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white" disabled={isSubmittingSupport || isUploading} onClick={submitSupportTicket}>
                  {isSubmittingSupport ? "Envoi..." : "Envoyer la demande"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Button onClick={handleSave} disabled={isSaving || isUploading || isLocked || user?.metadataStatus === 'en_attente'} className="gap-2 shrink-0 h-12 px-6 rounded-xl font-bold shadow-md">
            {saveSuccess ? <CheckCircle2 className="w-5 h-5" /> : <Save className="w-5 h-5" />}
            {isSaving ? "Enregistrement..." : isUploading ? "Upload en cours..." : saveSuccess ? "Enregistré" : "Enregistrer le profil"}
          </Button>
        </div>
      </div>

      {history.length > 0 && (
        <div className="space-y-3 mb-6 animate-in fade-in">
          <h3 className="font-semibold text-emerald-800 flex items-center gap-2 mb-2">
            <MessageCircle className="w-5 h-5 text-emerald-600" />
            Retours de l'administration et historique
          </h3>
          <div className="space-y-3">
            {history.map(h => (
              <div key={h.id} className={`p-4 rounded-xl border ${h.action_type === 'PARTNER_REACTION' ? 'bg-blue-50/50 border-blue-100' : 'bg-red-50/30 border-red-100'} shadow-sm flex flex-col gap-2`}>
                <div className="flex justify-between items-center text-xs">
                  <span className={`font-bold ${h.action_type === 'PARTNER_REACTION' ? 'text-blue-700' : 'text-red-700'}`}>
                    {h.action_type === 'REJECT' ? 'Rejet' : h.action_type === 'REQUIRE_PROOF' ? 'Demande de preuve' : h.action_type === 'APPROVE' ? 'Validation' : 'Votre réponse'}
                  </span>
                  <span className="text-muted-foreground">{new Date(h.created_at).toLocaleString('fr-FR')}</span>
                </div>
                <p className="text-sm text-foreground">{h.message}</p>
                {h.document_url && (
                  <a href={h.document_url} target="_blank" rel="noopener noreferrer" className="inline-flex w-fit items-center gap-1 mt-1 text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline">
                    <FileText className="w-3 h-3" /> Voir la pièce jointe
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {supportTickets.length > 0 && (
        <div className="space-y-3 mb-6 animate-in fade-in">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-blue-800 flex items-center gap-2">
              <LifeBuoy className="w-5 h-5 text-blue-600" />
              Historique de vos demandes de support
            </h3>
            <Button variant={showArchivedSupport ? "secondary" : "ghost"} size="sm" onClick={() => setShowArchivedSupport(!showArchivedSupport)} className="text-xs h-7">
              {showArchivedSupport ? "← Afficher requêtes actives" : "Voir archives"}
            </Button>
          </div>
          
          {supportTickets.filter(t => showArchivedSupport ? t.status === 'closed' : t.status !== 'closed').length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground bg-muted/20 border border-dashed rounded-xl">
              Aucune demande {showArchivedSupport ? "archivée" : "active"}.
            </div>
          ) : (
            <div className="space-y-3">
              {supportTickets.filter(t => showArchivedSupport ? t.status === 'closed' : t.status !== 'closed').map(ticket => (
              <div key={ticket.id} className="p-4 rounded-xl border border-border shadow-sm flex flex-col gap-2">
                <div className="flex justify-between items-center text-xs">
                  <span className="font-bold text-blue-700">Sujet : {ticket.subject}</span>
                  <span className="text-muted-foreground">{new Date(ticket.created_at).toLocaleString('fr-FR')}</span>
                </div>
                <p className="text-sm text-foreground">{ticket.message}</p>
                {ticket.document_url && (
                  <a href={ticket.document_url} target="_blank" rel="noopener noreferrer" className="inline-flex w-fit items-center gap-1 mt-1 text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline">
                    <FileText className="w-3 h-3" /> Voir la pièce jointe
                  </a>
                )}
                {ticket.status === 'resolved' && (
                  <div className="mt-2 pt-2 border-t border-emerald-200 text-emerald-800 bg-emerald-50 p-2 rounded text-sm relative">
                    <span className="font-bold block text-xs mb-1">Réponse du Support Kayn'in :</span>
                    {ticket.admin_response}
                    
                    <div className="flex gap-2 mt-3 pt-2 border-t border-emerald-200/50">
                        {ticket.status !== 'closed' && (
                          <Button size="sm" variant="outline" className="flex-1 text-xs h-7 border-emerald-300 hover:bg-emerald-100" onClick={async () => {
                            if (!confirm("Voulez-vous archiver ce ticket pour libérer l'espace ?")) return;
                            const { archiveSupportTicket } = await import('@/data/moderationStore');
                            await archiveSupportTicket(ticket.id);
                            fetchSupportTickets().then(setSupportTickets);
                            toast({description: "Ticket archivé."});
                          }}>
                            Archiver
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="flex-1 text-xs h-7 border-red-200 text-red-600 hover:bg-red-50" onClick={async () => {
                          if (!confirm("Supprimer définitivement ce ticket ?")) return;
                          const { deleteSupportTicket } = await import('@/data/moderationStore');
                          await deleteSupportTicket(ticket.id);
                          fetchSupportTickets().then(setSupportTickets);
                          toast({description: "Ticket supprimé."});
                        }}>
                          Supprimer
                        </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            </div>
          )}
        </div>
      )}

      {user?.metadataStatus === 'en_attente' && (
        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-xl shadow-sm mb-6 animate-in fade-in flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div>
            <h3 className="text-amber-800 font-bold text-sm">Modifications en cours de validation</h3>
            <p className="text-amber-700 text-xs mt-1">Vos dernières modifications ont bien été transmises et sont en attente d'approbation par un administrateur. Pendant ce temps, vous ne pouvez pas soumettre de nouveaux changements.</p>
          </div>
        </div>
      )}

      {user?.metadataStatus === 'rejete' && user?.metadataFeedback && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-xl shadow-sm mb-6 animate-in fade-in flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <div>
            <h3 className="text-red-800 font-bold text-sm">Modifications rejetées</h3>
            <p className="text-red-700 text-xs mt-1 mb-2">Vos modifications ont été refusées pour la raison suivante :</p>
            <p className="bg-white p-2 text-sm text-red-900 border border-red-100 rounded italic">{user.metadataFeedback}</p>
            <p className="text-red-700 text-xs mt-2">Vous pouvez corriger vos informations et soumettre une nouvelle demande.</p>
          </div>
        </div>
      )}

      {isLocked && user?.metadataStatus !== 'en_attente' && (
        <div className={`mb-6 p-4 rounded-xl border text-sm flex gap-3 items-start ${
          user?.unlockRequestStatus === 'pending' ? 'bg-amber-50 border-amber-200 text-amber-800' :
          user?.unlockRequestStatus === 'rejected' ? 'bg-red-50 border-red-200 text-red-800' :
          'bg-orange-50 border-orange-200 text-orange-800'
        }`}>
          <ShieldAlert className="w-5 h-5 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-bold mb-1">
              {user?.unlockRequestStatus === 'pending' ? 'Demande de déverrouillage en cours' :
               user?.unlockRequestStatus === 'rejected' ? 'Demande de déverrouillage rejetée' :
               'Profil verrouillé (Modification soumise à autorisation)'}
            </p>
            <p>
              {user?.unlockRequestStatus === 'pending' ? "Votre demande est en cours d'examen par l'administration. Vous serez notifié dès qu'une décision sera prise." :
               user?.unlockRequestStatus === 'rejected' ? "L'administration a refusé votre demande de déverrouillage." :
               "Vous avez déjà effectué votre modification autorisée. Pour toute nouvelle modification, l'aval de l'administration est impératif."}
            </p>
            
            {user?.unlockRequestStatus === 'rejected' && user?.unlockRequestFeedback && (
              <div className="mt-2 p-2 bg-white rounded border border-red-100 text-red-900 italic">
                <strong>Motif :</strong> {user.unlockRequestFeedback}
              </div>
            )}

            {user?.unlockRequestStatus !== 'pending' && (
              <div className="mt-3">
                <Dialog open={isUnlockModalOpen} onOpenChange={setIsUnlockModalOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="gap-2 bg-orange-600 hover:bg-orange-700 text-white border-none">
                      <Key className="w-4 h-4" /> Demander le déverrouillage
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Demande de déverrouillage</DialogTitle>
                      <DialogDescription>
                        Expliquez pourquoi vous avez besoin de modifier à nouveau votre profil. L'administration examinera votre demande.
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleUnlockRequest} className="space-y-4 pt-4">
                      <div className="space-y-2">
                        <label className="text-sm font-semibold">Objet de la demande</label>
                        <Input required value={unlockSubject} onChange={e => setUnlockSubject(e.target.value)} placeholder="Ex: Mise à jour du logo officiel" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold">Explications détaillées</label>
                        <Textarea required value={unlockMessage} onChange={e => setUnlockMessage(e.target.value)} placeholder="Veuillez détailler les éléments que vous souhaitez modifier..." className="h-32" />
                      </div>
                      <div className="flex justify-end pt-2">
                        <Button type="submit" disabled={isSaving || !unlockSubject.trim() || !unlockMessage.trim()}>
                          {isSaving ? "Envoi en cours..." : "Envoyer la demande"}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            )}
          </div>
        </div>
      )}

      <div className={`grid grid-cols-1 lg:grid-cols-3 gap-8 ${(user?.metadataStatus === 'en_attente' || isLocked) ? 'opacity-70 pointer-events-none' : ''}`}>
        
        {/* Colonne Principale (Infos générales) */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="border-border shadow-sm rounded-2xl overflow-hidden">
            <div className="h-2 w-full bg-gradient-to-r from-primary to-accent" />
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <FileText className="w-5 h-5 text-primary" />
                Présentation Détaillée
              </CardTitle>
              <CardDescription>Décrivez votre mission, vision et impact.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <Label className="font-semibold text-sm">Description globale *</Label>
                <Textarea 
                  placeholder={`Présentez l'histoire et la mission de ${roleLabel}...`}
                  rows={5}
                  value={formData.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  className="resize-none rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label className="font-semibold text-sm">Statut juridique / Forme</Label>
                <Input 
                  placeholder="Ex: Association à but non lucratif, Parti Politique..."
                  value={formData.statut}
                  onChange={(e) => handleChange("statut", e.target.value)}
                  className="rounded-xl h-12"
                />
              </div>

              <div className="space-y-2">
                <Label className="font-semibold text-sm">Activités principales</Label>
                <Textarea 
                  placeholder="Quelles sont vos principales actions sur le terrain ?"
                  rows={3}
                  value={formData.activites}
                  onChange={(e) => handleChange("activites", e.target.value)}
                  className="resize-none rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label className="font-semibold text-sm">Contribution à la démocratie</Label>
                <Textarea 
                  placeholder="Comment favorisez-vous l'engagement citoyen ?"
                  rows={3}
                  value={formData.contribution}
                  onChange={(e) => handleChange("contribution", e.target.value)}
                  className="resize-none rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label className="font-semibold text-sm">Projets en cours / Perspectives</Label>
                <Textarea 
                  placeholder="Vos projets majeurs actuels ou futurs..."
                  rows={3}
                  value={formData.projets}
                  onChange={(e) => handleChange("projets", e.target.value)}
                  className="resize-none rounded-xl"
                />
              </div>
            </CardContent>
          </Card>

          {/* Spécifique ONG */}
          {isOng && (
            <Card className="border-border shadow-sm rounded-2xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl text-emerald-600">
                  <Building className="w-5 h-5" />
                  Informations Associatives
                </CardTitle>
                <CardDescription>Détails sur l'intervention de l'ONG.</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="font-semibold text-sm">Niveau d'intervention</Label>
                  <Select value={formData.ongLevel} onValueChange={(val) => handleChange("ongLevel", val)}>
                    <SelectTrigger className="w-full h-12 rounded-xl">
                      <SelectValue placeholder="Sélectionnez..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="national">National</SelectItem>
                      <SelectItem value="regional">Régional</SelectItem>
                      <SelectItem value="local">Local</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="font-semibold text-sm">Siège principal</Label>
                  <Input 
                    placeholder="Ville, Région..."
                    value={formData.ongSiege}
                    onChange={(e) => handleChange("ongSiege", e.target.value)}
                    className="rounded-xl h-12"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Spécifique Média */}
          {isMedia && (
            <Card className="border-border shadow-sm rounded-2xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl text-purple-600">
                  <Tv className="w-5 h-5" />
                  Identité du Programme / Émission
                </CardTitle>
                <CardDescription>Veuillez renseigner le nom dans les différentes langues.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="space-y-2">
                  <Label className="font-semibold text-sm">Titre (Français)</Label>
                  <Input 
                    value={formData.mediaTitleFr}
                    onChange={(e) => handleChange("mediaTitleFr", e.target.value)}
                    className="rounded-xl h-12"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label className="font-semibold text-sm">Titre (Arabe)</Label>
                    <Input 
                      value={formData.mediaTitleAr}
                      onChange={(e) => handleChange("mediaTitleAr", e.target.value)}
                      className="rounded-xl h-12 text-right"
                      dir="rtl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-semibold text-sm">Titre (Tamazight)</Label>
                    <Input 
                      value={formData.mediaTitleZgh}
                      onChange={(e) => handleChange("mediaTitleZgh", e.target.value)}
                      className="rounded-xl h-12"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Colonne Latérale (Visuel et Réseaux) */}
        <div className="space-y-6">
          <Card className="border-border shadow-sm rounded-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Camera className="w-5 h-5 text-primary" />
                Identité Visuelle
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <Label className="font-semibold text-sm">Logo (URL ou Upload)</Label>
                {formData.logoUrl && (
                  <div className="mb-2 w-full h-32 rounded-xl bg-muted/50 border border-border flex items-center justify-center p-2">
                    <img src={formData.logoUrl} alt="Logo" className="max-h-full max-w-full object-contain" />
                  </div>
                )}
                <div className="flex gap-2">
                  <Input 
                    placeholder="Lien URL..."
                    value={formData.logoUrl}
                    onChange={(e) => handleChange("logoUrl", e.target.value)}
                    className="flex-1 h-10 rounded-lg text-sm"
                  />
                  <Label className="cursor-pointer bg-primary text-primary-foreground hover:bg-primary/90 px-3 rounded-lg flex items-center justify-center font-semibold text-xs transition-colors shrink-0">
                    Upload
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFileUpload(e, 'logoUrl')} />
                  </Label>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="font-semibold text-sm">Photo Représentative</Label>
                {formData.image1Url && (
                  <div className="mb-2 w-full h-32 rounded-xl bg-muted/50 border border-border flex items-center justify-center overflow-hidden">
                    <img src={formData.image1Url} alt="Image 1" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="flex gap-2">
                  <Input 
                    placeholder="Lien URL..."
                    value={formData.image1Url}
                    onChange={(e) => handleChange("image1Url", e.target.value)}
                    className="flex-1 h-10 rounded-lg text-sm"
                  />
                  <Label className="cursor-pointer bg-secondary text-secondary-foreground hover:bg-secondary/80 px-3 rounded-lg flex items-center justify-center font-semibold text-xs transition-colors shrink-0">
                    Upload
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFileUpload(e, 'image1Url')} />
                  </Label>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border shadow-sm rounded-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <LinkIcon className="w-5 h-5 text-primary" />
                Liens & Réseaux Sociaux
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center bg-background rounded-xl border border-input pr-3 overflow-hidden h-11 focus-within:ring-2 focus-within:ring-primary/20 transition-all">
                <div className="bg-slate-800 text-white h-full w-11 flex items-center justify-center shrink-0"><Globe className="w-4 h-4"/></div>
                <input value={formData.website} onChange={e=>handleChange('website', e.target.value)} placeholder="Site Web Officiel" className="flex-1 h-full bg-transparent px-3 text-sm outline-none" />
              </div>
              <div className="flex items-center bg-background rounded-xl border border-input pr-3 overflow-hidden h-11 focus-within:ring-2 focus-within:ring-primary/20 transition-all">
                <div className="bg-[#1877F2] text-white h-full w-11 flex items-center justify-center shrink-0"><Facebook className="w-4 h-4"/></div>
                <input value={formData.facebook} onChange={e=>handleChange('facebook', e.target.value)} placeholder="Page Facebook" className="flex-1 h-full bg-transparent px-3 text-sm outline-none" />
              </div>
              <div className="flex items-center bg-background rounded-xl border border-input pr-3 overflow-hidden h-11 focus-within:ring-2 focus-within:ring-primary/20 transition-all">
                <div className="bg-black text-white h-full w-11 flex items-center justify-center shrink-0"><XIcon className="w-4 h-4"/></div>
                <input value={formData.twitter} onChange={e=>handleChange('twitter', e.target.value)} placeholder="Lien X" className="flex-1 h-full bg-transparent px-3 text-sm outline-none" />
              </div>
              <div className="flex items-center bg-background rounded-xl border border-input pr-3 overflow-hidden h-11 focus-within:ring-2 focus-within:ring-primary/20 transition-all">
                <div className="bg-gradient-to-tr from-[#f9ce34] via-[#ee2a7b] to-[#6228d7] text-white h-full w-11 flex items-center justify-center shrink-0"><Instagram className="w-4 h-4"/></div>
                <input value={formData.instagram} onChange={e=>handleChange('instagram', e.target.value)} placeholder="Compte Instagram" className="flex-1 h-full bg-transparent px-3 text-sm outline-none" />
              </div>
              <div className="flex items-center bg-background rounded-xl border border-input pr-3 overflow-hidden h-11 focus-within:ring-2 focus-within:ring-primary/20 transition-all">
                <div className="bg-[#FF0000] text-white h-full w-11 flex items-center justify-center shrink-0"><Youtube className="w-4 h-4"/></div>
                <input value={formData.youtube} onChange={e=>handleChange('youtube', e.target.value)} placeholder="Chaîne YouTube" className="flex-1 h-full bg-transparent px-3 text-sm outline-none" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
